import {PAYMENT_MODE_OPTIONS} from './payment-method-details';
/**
 * Billing entry view helpers (PATIENT-PAYMENTS-REVENUE-SPINE-V1).
 *
 * Pure, react-free logic for the /dashboard/billing-entry surface: the
 * role-gate decision (mirrors workboard-view), the INTEGER-MINOR-UNITS
 * rupee parser (string math -- a float never touches money), and display
 * formatting. All wire types mirror api/routes/patient-payments.ts.
 */

import type { DashboardMe } from '@/dashboard/types';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { formatTimestampIst } from '@/lib/datetime-ist';

// ---- Wire types (PHI-carved mirrors of the backend responses) ------------

export interface BillingClinicOption {
  readonly clinicId: number;
  readonly clinicLabel: string | null;
}

export interface BillingEntryContext {
  readonly reconciliationByClinic?: Readonly<Record<string,boolean>>;
  readonly consultationActionsByClinic?: Readonly<Record<string, { readonly canWaive: boolean; readonly canReverseWaiver: boolean }>>;
  readonly clinics: readonly BillingClinicOption[];
  readonly defaultClinicId: number | null;
  readonly canVoid: boolean;
  /** Per-clinic category vocabulary; a clinic absent here renders NO
   *  category field (the backend fail-closes to null -- never free text). */
  readonly categoriesByClinic: Readonly<Record<string, readonly string[]>>;
  /** Q-13=B (RECEPTION-OS-BUILD-V1): the org-configured walk-in
   *  consultation fee per clinic, INTEGER MINOR UNITS, narrowed to the
   *  viewer's clinics server-side. A clinic absent here renders NO
   *  prefill affordance (seed {}; a fee is never invented). */
  readonly walkinConsultationFeeByClinic: Readonly<Record<string, number>>;
}

/** Q-13=B: the amount-input string for a configured fee -- whole rupees
 *  without decimals when even ('300'), paisa-precise otherwise ('300.50');
 *  round-trips through parseInrToMinorUnits exactly. */
export function walkinFeeInputValue(minorUnits: number): string {
  return minorUnits % 100 === 0
    ? String(minorUnits / 100)
    : (minorUnits / 100).toFixed(2);
}

export interface PatientPaymentRow {
  readonly receiptSnapshot?: {
    readonly clinicAddress: string | null;
    readonly clinicPhone: string | null;
    readonly clinicEmail: string | null;
    readonly clinicLogo: string | null;
    readonly organizationName?: string | null;
    readonly recordingStaffName?: string | null;
    readonly recordingStaffRole?: string | null;
    readonly settlement: {
      readonly allocations: readonly {billId:number;amountMinorUnits:number;dueAfterMinorUnits:number}[];
      readonly unpaidAfterMinorUnits:number;
      readonly unappliedCreditMinorUnits:number;
    };
  };
  readonly receiptDetails?: {
    readonly patientAddress?: string | null;
    readonly patientUmr?: string | null;
    readonly demographics?: { readonly ageYears: number | null; readonly ageIsApproximate: boolean; readonly gender: string | null };
    readonly clinicName: string | null;
    readonly consultantName: string | null;
    readonly appointmentAt: string | null;
    readonly sourceState: 'available' | 'unavailable';
  };
  readonly id: number;
  readonly clinicId: number;
  readonly patientId: string;
  readonly patientName: string | null;
  readonly appointmentId: number | null;
  readonly amountMinorUnits: number;
  readonly currency: string;
  readonly method: string | null;
  readonly category: string | null;
  /** Payment reference token (BILLING-MISSING-DELTA-V1, "Other Payment
   *  Fields"). OPTIONAL so pre-package fixtures stay valid; null = not
   *  recorded (or the 20260830_1 column is pre-apply). */
  readonly referenceNumber?: string | null;
  readonly methodSubtype?: string | null;
  readonly methodOtherName?: string | null;
  readonly occurredAt: string;
  readonly createdAt: string;
  readonly voidedAt: string | null;
  readonly voidReason: string | null;
  /** WHO voided this payment (PAYMENT-WHO-VOIDED-TO-WIRE-V1) -- the structural
   *  mirror of the backend PatientPaymentWire.voidedByLabel, resolved on the
   *  recent-payments operator list (coc/owner-tier). null on an active row;
   *  OMITTED on the single-payment record/void responses. */
  readonly voidedByLabel?: string | null;
}

export interface RecentPatientPaymentsResponse {
  readonly scope?: { readonly patientId: string | null; readonly patientFamilyIds: readonly string[] | null; readonly clinicId: number | null; readonly paymentId: number | null };
  /** NULL on a PATIENT-narrowed read (L-17(b)): that list spans the caller's
   *  lawful belt, so no single clinic can honestly be named. */
  readonly clinicId: number | null;
  readonly payments: readonly PatientPaymentRow[];
  /** B1: the window this page answers, and whether OLDER receipts exist
   *  beyond it. OPTIONAL so a pre-corrective payload keeps rendering. */
  readonly offset?: number;
  readonly limit?: number;
  readonly hasMore?: boolean;
}

export interface RecordPatientPaymentResponse {
  readonly payment: PatientPaymentRow;
  readonly created: boolean;
}

export const PAYMENT_METHOD_OPTIONS = PAYMENT_MODE_OPTIONS;

export const VOID_REASON_OPTIONS = ['entry_error', 'duplicate', 'other'] as const;

// ---- Role gate (UX cleanliness; backend config-backed gate is the real
// boundary. The FE set approximates the seeded default + supervisors -- an
// org that retargets the entry role to an exotic role still works through
// the backend; recorded honest gap.) ---------------------------------------

export const BILLING_ENTRY_ROLES: ReadonlySet<DashboardMe['role']> = new Set<
  DashboardMe['role']
>(['receptionist', 'coc', 'owner']);

export type BillingEntryAccessDecision =
  | { readonly kind: 'wait' }
  | { readonly kind: 'redirect'; readonly to: string }
  | { readonly kind: 'render' };

export function decideBillingEntryAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): BillingEntryAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) return { kind: 'redirect', to: '/login' };
  if (!BILLING_ENTRY_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// ---- Money: INTEGER MINOR UNITS via string math ---------------------------

const INR_INPUT_RE = /^(\d{1,13})(?:\.(\d{1,2}))?$/;

/**
 * Parse operator rupee input ("12,500", "12500.50", "1 250") into INTEGER
 * MINOR UNITS (paise). String math only -- no float multiplication anywhere.
 * Returns null for anything that is not a positive amount with at most two
 * decimal places.
 */
export function parseInrToMinorUnits(raw: string): number | null {
  const cleaned = raw.replace(/[,\s]/g, '');
  const match = INR_INPUT_RE.exec(cleaned);
  if (match === null) return null;
  const rupees = Number.parseInt(match[1]!, 10);
  const paise = Number.parseInt((match[2] ?? '').padEnd(2, '0') || '0', 10);
  if (!Number.isSafeInteger(rupees) || !Number.isSafeInteger(paise)) return null;
  const minor = rupees * 100 + paise;
  if (!Number.isSafeInteger(minor) || minor <= 0) return null;
  return minor;
}

/** Display formatting: '₹ 12,500' / '₹ 12,500.50' (en-IN grouping).
 *  Documents request two decimal places; existing screen consumers keep their default.
 *  Integer division/remainder only -- display never re-derives money. */
export function formatMinorUnitsInr(minorUnits: number, minimumFractionDigits: 0 | 2 = 0): string {
  const rupees = Math.trunc(minorUnits / 100);
  const paise = Math.abs(minorUnits % 100);
  const base = `₹ ${rupees.toLocaleString('en-IN')}`;
  return paise === 0 && minimumFractionDigits === 0 ? base : `${base}.${String(paise).padStart(2, '0')}`;
}

/**
 * Hero-grain compact form beside THE ONE formatter (OWNER-OPERATING-SYSTEM-V1;
 * the FORMATTER LAW, growth brief s.20.5): compact lakh notation ('₹ 92.4L')
 * at hero/card grain ONLY -- full precision stays at row grain and on print
 * surfaces via formatMinorUnitsInr. Below one lakh the full form renders (a
 * compact form of a small number hides money). Integer-safe: derives from
 * minor units with one division at display time, never re-deriving money.
 */
export function formatMinorUnitsInrCompact(minorUnits: number): string {
  const rupees = Math.trunc(minorUnits / 100);
  if (Math.abs(rupees) < 100000) {
    return formatMinorUnitsInr(minorUnits);
  }
  const lakhTenths = Math.round((rupees / 100000) * 10);
  const whole = Math.trunc(lakhTenths / 10);
  const tenth = Math.abs(lakhTenths % 10);
  return `₹ ${whole.toLocaleString('en-IN')}.${tenth}L`;
}

/** Local-input value ('YYYY-MM-DDTHH:MM') -> ISO, or null when unparsable. */
export function occurredAtIsoFromLocalInput(value: string): string | null {
  if (value.trim() === '') return null;
  const t = Date.parse(value);
  if (Number.isNaN(t)) return null;
  return new Date(t).toISOString();
}

/**
 * COLLECT-PAYMENT-V1 -- WHAT CLOCK IS THE OPERATOR TYPING ON?
 *
 * `<input type="datetime-local">` renders the BROWSER-LOCAL wall clock and
 * carries no zone. The receipt and the recent-payments table both render
 * IST. On an IST device those agree and there is nothing to explain; on any
 * other device the operator types one clock and reads another back, and the
 * screen used to say nothing at all.
 *
 * This is a LABEL, not a conversion: the instant is already handled by
 * `occurredAtIsoFromLocalInput` (typed wall clock -> absolute instant, in
 * the browser's own zone) and displayed by `formatTimestampIst` -- THE ONE
 * display formatter, reused here rather than re-derived. No second date
 * rule is authored and no time policy is invented.
 *
 * Returns '' for an empty or unparseable value: an input the operator is
 * still typing gets no scolding label, and the submit path already refuses
 * an invalid received time.
 */
export function occurredAtZoneLabel(localInputValue: string): string {
  const iso = occurredAtIsoFromLocalInput(localInputValue);
  if (iso === null) return '';
  const ist = formatTimestampIst(iso);
  // The device's own zone name, when the browser will tell us. A device
  // already on IST needs no translation line -- saying it twice is noise.
  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    zone = '';
  }
  if (zone === 'Asia/Kolkata' || zone === 'Asia/Calcutta') {
    return `Your device clock is IST - this is ${ist}.`;
  }
  const named = zone === '' ? 'your device clock' : `${zone}`;
  return `Times are entered on ${named} and recorded as ${ist}.`;
}

export function clinicOptionLabel(option: BillingClinicOption): string {
  return clinicLabelOf(option.clinicLabel);
}

/**
 * THE ONE reference-token-for-the-wire rule (BILLING-MISSING-DELTA-V1,
 * "Other Payment Fields"). An operator's raw input becomes either a trimmed
 * IDENTIFIER or null -- never an empty string, which the writer would have to
 * disambiguate. Extracted from BillingEntryPage so the collect drawer and the
 * full form send the SAME shape; the FORMAT itself stays server-validated
 * (PAYMENT_REFERENCE_RE, lib/patient-payments.ts) -- a client-side copy of
 * that regex would be a second rule with its own drift.
 */
export function referenceNumberForWire(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/** The reference field's label + placeholder, shared by every host that
 *  captures one, so the two surfaces can never name the same field
 *  differently. */
export const PAYMENT_REFERENCE_LABEL = 'Reference number';
export const PAYMENT_REFERENCE_PLACEHOLDER = 'UPI ref / cheque no / txn id';
/** Mirrors the server's 64-character bound (patient_payments_reference_number_chk). */
export const PAYMENT_REFERENCE_MAX_LENGTH = 64;

export function methodLabel(method: string | null): string {
  if (method === null || method === '') return '-';
  return method.replace(/_/g, ' ');
}

/**
 * THE PATIENT-FACING form of a payment mode: "UPI", "Bank transfer", "Cash".
 *
 * WHY IT IS A SECOND FUNCTION AND NOT A CHANGE TO `methodLabel`. A payment
 * mode has two audiences. On an OPERATOR surface -- the recent-payments
 * table, the record's evidence rows, the drawer's mode picker -- `methodLabel`
 * renders the stored token nearly verbatim, and four shipped screens plus
 * their tests depend on exactly that. On a PATIENT surface -- a printed
 * receipt -- "upi" is the database's spelling showing through onto a document
 * somebody is handed.
 *
 * Changing `methodLabel` itself would have re-cased all four operator screens,
 * which is a product decision nobody made: the reviewer's H3 ruling was about
 * the RECEIPT. So the display form is its own named export, HERE beside the
 * vocabulary it belongs to rather than copied into a component -- one module
 * owns how a payment mode is spelled, in both of its voices.
 *
 * THE ACRONYM SET IS BOUNDED AND EXPLICIT, never a general
 * all-caps-if-short rule, which would shout at "card".
 *
 * DECLARED INCONSISTENCY, not silently left: an operator reading the payments
 * table still sees "upi" while the receipt printed from that same row says
 * "UPI". Whether the operator screens should follow is a wording decision for
 * review, and it is raised in this package's FOR FACTORY handoff.
 */
const PAYMENT_METHOD_ACRONYMS: Readonly<Record<string, string>> = { upi: 'UPI' };

export function methodDisplayLabel(method: string | null): string {
  if (method === null || method === '') return '-';
  const spaced = method.replace(/_/g, ' ').trim();
  if (spaced === '') return '-';
  const acronym = PAYMENT_METHOD_ACRONYMS[spaced.toLowerCase()];
  if (acronym !== undefined) return acronym;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Humanize a controlled category token (the per-clinic category vocabulary) for
 * display: snake_case -> "Sentence case". No dental/India assumption -- purely
 * formats the stored token (RECEPTIONIST-EXPERIENCE-STITCH-V1, F5).
 */
export function categoryLabel(category: string | null): string {
  if (category === null || category === '') return '-';
  const spaced = category.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---- The billing queue (MONEY-COMPLETION-V1, V3-P3; rider R-10) -----------
//
// Wire mirrors of lib/billing-queue.ts (the backend owns the ONE concept-5
// netting; this surface DISPLAYS wire rows and never re-computes -- the
// evidence-list law).

export type BillingQueueEpisodeState =
  | 'outstanding'
  | 'not_configured'
  | 'settled'
  | 'no_accepted_plan'
  | 'not_closed'
  | 'terminal';

export interface FinalizedBillSummary {
  readonly state:'none'|'known'|'allocation_unknown'|'unavailable';
  readonly billCount:number;
  readonly paidMinorUnits:number|null;
  readonly dueMinorUnits:number|null;
}

export interface BillingQueueRow {
  readonly finalizedBills?: FinalizedBillSummary;
  readonly appointmentId: number;
  readonly appointmentTimeIso: string;
  readonly clinicId: number;
  readonly patientLabel: string | null;
  readonly patientId: string | null;
  readonly leadId: number | null;
  readonly state: BillingQueueEpisodeState;
  readonly acceptedCount: number;
  /** Whole INR; null when no accepted item carries a value. */
  readonly acceptedValuedSumInr: number | null;
  readonly notConfiguredCount: number;
  readonly collectedMinorUnits: number;
  readonly outstandingMinorUnits: number | null;
  readonly ageDays: number;
  readonly doorAppointmentId: number;
  readonly doorSuggestedAmountInr: number | null;
}

export interface BillingQueueSummary {
  readonly rowCount: number;
  readonly outstandingTotalMinorUnits: number;
  readonly notConfiguredRowCount: number;
  readonly capped: boolean;
}

export interface BillingQueueResponse {
  readonly clinicIds: readonly number[];
  readonly rows: readonly BillingQueueRow[];
  readonly summary: BillingQueueSummary;
  /** RECEPTION-MONEY-TRUTH-V1 (A2): the patient-grain money block, present
   *  ONLY on a PATIENT-NARROWED read (the R-Q04 view's summary line) --
   *  THE ONE patient-grain owner's wire (lib/patient-money.ts). Absent on
   *  queue-mode reads and on any degraded read (honest omission). */
  readonly patientMoney?: {
    readonly paidToDateMinorUnits: number;
    readonly unlinkedPaidMinorUnits: number;
    readonly owedMinorUnits: number;
    readonly owedNotConfiguredCount: number;
    readonly carriedMinorUnits: number;
    readonly carriedNotConfiguredCount: number;
    readonly todayOwedMinorUnits: number;
    readonly collectedTodayMinorUnits: number;
    readonly lastPayment: {
      readonly occurredAtIso: string;
      readonly amountMinorUnits: number;
      readonly method: string | null;
    } | null;
    readonly capped: boolean;
  };
}

/** The NULL-AMOUNT OBLIGATION LAW's display string (never Rs 0). */
export const ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY =
  'Accepted amount not configured';

/** Age column: whole IST clinic-days ("today" / "1 day" / "N days"). */
export function queueAgeLabel(ageDays: number): string {
  if (ageDays <= 0) return 'today';
  return ageDays === 1 ? '1 day' : `${ageDays} days`;
}

/** The episode state's honest display line (the convert-moment card). */
export function episodeStateLabel(state: BillingQueueEpisodeState): string {
  switch (state) {
    case 'outstanding':
      return 'Outstanding on this visit';
    case 'not_configured':
      return ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY;
    case 'settled':
      return 'Collected in full for this visit';
    case 'no_accepted_plan':
      return 'No accepted treatment on this visit';
    case 'not_closed':
      return 'Visit not closed yet - accepted treatment becomes collectable at close';
    case 'terminal':
      return 'Cancelled / no-show visit - not a collectable obligation';
  }
}

// --- FRONT-DESK-CORRECTIVE-V1 (L-07 + L-02): THE VISIT-LINK LAW ------------
//
// THE FOUNDER INVARIANT, verbatim: "The operator must know where the money is
// going before Save."
//
// She arrived through the day-board door on appointment #253, recorded
// Rs 300 (linked), the form reset and DROPPED the link, and the Rs 200 she
// recorded next was written unallocated -- skipped by the netting at
// api/routes/appointments-read.ts:1449, so the board kept asking a paid
// patient to pay again. She did nothing wrong and the screen gave her no
// signal.
//
// WHAT THIS DOES NOT DECIDE: what unallocated money MEANS -- a patient-account
// credit, or an obligation to bind every payment to an episode -- is
// gap.f-20-unallocated-payment-policy (founder/tenant). This binds the link
// the operator already opened, and makes an invisible state VISIBLE. It
// allocates nothing, backfills nothing, and changes no arithmetic.

/** The unallocated state's line. Rendered BEFORE Save, never after. */
export const BILLING_LINK_UNALLOCATED_LINE =
  'Recording as UNALLOCATED - not against any visit';

/**
 * Everything that can move the armed visit context, enumerated -- so a
 * SILENT change of link state is a compile error, not a defect report.
 *
 * EXPLICIT EXITS (the only two acts that may drop or move the link):
 *   - 'remove-link'   the operator presses the existing "Remove link" act
 *   - 'queue-collect' the operator picks another episode's Collect door
 *                     (a null doorAppointmentId is an explicit unallocated
 *                     pick, and it is SAID)
 *
 * NOT EXITS -- the link is RETAINED through every one of these:
 *   - 'payment-recorded'  a successful write
 *   - 'record-another'    the ratified re-arm (FLOW-ARCHITECTURE.md:4951-4961
 *                         ratifies the receipt replacing the form, this act
 *                         being the only way back, and the receipt naming the
 *                         exact appointment or "No visit link" -- dropping
 *                         the link is NOT among the three)
 * ...and no timeout, navigation side effect or silent reset exists here at
 * all, because there is no branch for one.
 */
export type BillingLinkEvent =
  | { readonly kind: 'payment-recorded' }
  | { readonly kind: 'record-another' }
  | { readonly kind: 'remove-link' }
  | { readonly kind: 'queue-collect'; readonly appointmentId: number | null };

/** THE ONE rule for where the money is going next. */
export function nextLinkedAppointment(
  current: number | null,
  event: BillingLinkEvent,
): number | null {
  switch (event.kind) {
    case 'remove-link':
      return null;
    case 'queue-collect':
      return event.appointmentId;
    case 'payment-recorded':
    case 'record-another':
      // RETAINED (founder ruling 2026-09-03). The next payment on this
      // workflow stays attached to the visit she opened.
      return current;
  }
}

/** Amount wording from the approved b4d receipt. Integer paise only. */
export function paymentAmountInWords(minorUnits: number, currency = 'INR'): string {
  if (currency.toUpperCase() !== 'INR' || !Number.isSafeInteger(minorUnits) || minorUnits < 0 || minorUnits > 99_999_999_999) {
    return 'Amount in words unavailable';
  }
  const small = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const words = (n: number): string => {
    if (n < 20) return small[n]!;
    if (n < 100) return ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'][Math.floor(n / 10)]! + (n % 10 ? ` ${small[n % 10]}` : '');
    for (const [value, label] of [[10_000_000, 'crore'], [100_000, 'lakh'], [1000, 'thousand'], [100, 'hundred']] as const) {
      if (n >= value) return `${words(Math.floor(n / value))} ${label}${n % value ? ` ${words(n % value)}` : ''}`;
    }
    return '';
  };
  const rupees = Math.floor(minorUnits / 100), paise = minorUnits % 100;
  return `${words(rupees)} ${rupees === 1 ? 'rupee' : 'rupees'}${paise ? ` and ${words(paise)} ${paise === 1 ? 'paisa' : 'paise'}` : ''} only`;
}

export function receiptListMatchesScope(response: RecentPatientPaymentsResponse, expected: {
  patientId: string; clinicId: number; paymentId: number | null;
}): boolean {
  const scope = response.scope;
  const family = scope?.patientFamilyIds;
  return !!scope && scope.patientId === expected.patientId && scope.clinicId === expected.clinicId &&
    response.clinicId === expected.clinicId && scope.paymentId === expected.paymentId &&
    Array.isArray(family) && family.length > 0 && family.every(id => typeof id === 'string' && !!id) &&
    family.includes(expected.patientId) && Array.isArray(response.payments) && response.payments.every(payment =>
      payment && payment.clinicId === expected.clinicId && family.includes(payment.patientId) &&
      (expected.paymentId === null || payment.id === expected.paymentId));
}
